import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, Optional, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from '../../admin/services/admin.service';
import { BookingComponent } from '../../main/booking/booking.component';
import { OrderNotificationsComponent } from '../../main/order-notifications/order-notifications.component';
import { Client } from '../../models/client.model';
import { CrmForBooking } from '../../models/crm.model';
import { ObjectModel } from '../../models/object.model';
import { Status } from '../../models/status.model';
import { RoleService } from '../../role/role.service';
import { StatusesService } from '../../services/statuses.service';
import { catchError, map, Observable, of } from 'rxjs';

import { CRMService } from '../crm.service';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { FirebaseService } from '../../services/firebase.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-crm-details',
    templateUrl: './crm-details.component.html',
    styleUrls: ['./crm-details.component.scss'],
    standalone: false
})
export class CrmDetailsComponent implements OnInit {
  client: Client;

  company = this.adminService.local$.getValue();

  company_logo: string;

  crm: CrmForBooking;

  crmNotFound = false;

  forbidden = signal(false);
  
  id: number;
  
  loading = signal(false);
  
  statuses: Status[] = [];

  updatingCrm = signal(false);

  vehicle: ObjectModel;

  private _destroyRef = inject(DestroyRef);

  constructor(
    private activatedRoute: ActivatedRoute,
    public adminService: AdminService,
    private crmService: CRMService,
    private dialog: MatDialog,
    @Optional() private matDialogRef: MatDialogRef<CrmDetailsComponent>,
    public roleService: RoleService,
    private router: Router,
    private statusesService: StatusesService,
    public cdr: ChangeDetectorRef,
    private firebaseService: FirebaseService,
    private snackBar: MatSnackBar,
  ) {}
  

  get customActions() {
    if (this.crm?.booking?.checked_in) {
      return [];
    } else if (this.crm?.booking) {
      return [{ name: 'Reagendar Vehículo', action: this.createBooking.bind(this) }];
    } else {
      return [{ name: 'Agendar Vehículo', action: this.createBooking.bind(this) }];
    }
  }

  ngOnInit() {
    this.activatedRoute.params.forEach((params) => {
      if (params.id) {
        this.id = params.id;
        this.load().subscribe();
      }
    });

    this.company = this.adminService.local$.getValue();
    this.statusesService.statuses('Status::Crm::Booking').subscribe((statuses) => {
      this.statuses = statuses;
    });

    this.firebaseService.subscribeToMessages(
      'updated-document',
      { id: this.id, type: 'Crm::Booking' },
      this.handleUpdatedCrmMessage.bind(this),
      this._destroyRef
    );
  }

  load(): Observable<void> {
    this.loading.set(true);
    return this.crmService.get(this.id).pipe(
      catchError((error) => {
        console.log(error);
        if (error.status === 403) {
          this.forbidden.set(true);
        }
        return of(null);
      }),
    ).pipe( 
      map((response) => {
        this.cdr.markForCheck();
        this.loading.set(false);
        if (!response?.body || response?.status !== 200) {
          this.crmNotFound = true;
          return;
        }

        this.crm = response.body;
      })
    );
  }

  goBack() {
    if (this.matDialogRef instanceof MatDialogRef) {
      this.matDialogRef.close();
    } else {
      this.router.navigate(['main', 'crm', 'bookings']);
    }
  }

  goToClient(clientId: number) {
    this.router.navigate(['main', 'crm', 'clients', clientId]);
  }

  goToOrder(orderId: number) {
    this.router.navigate(['main', 'orders', orderId]);
  }

  openNotifications() {
    this.dialog.open(OrderNotificationsComponent, { data: { crm_id: this.id } });
  }

  createBooking() {
    let data = {};
    if (this.crm.booking) {
      data = { id: this.crm.booking.id };
    } else {
      data = {
        client: this.crm.client,
        object: this.crm.object,
        requirement_id: this.crm.requirement.id,
        from: 'crm',
        document_id: this.crm.id,
        document_type: 'Crm',
      };
    }

    this.dialog.open(BookingComponent, {
      minWidth: '80%',
      data,
    });
  }

  setStatus(status: number) {
    this.dialog.open(
      ConfirmDialogComponent,
      { data: { title: 'Actualizar Estado' } },
    ).afterClosed().subscribe((result) => {
      if (result) {
        this.cdr.markForCheck();
        this.updatingCrm.set(true);
        this.crmService.update(this.id, { status_id: status }).pipe(
          catchError((error) => {
            console.log(error);
            if (error.status === 403) {
              this.forbidden.set(true);
            }
            return of(null);
          }),
        ).subscribe((response) => {
          if (!response) return;
          Object.assign(this.crm, response.body);
          this.cdr.markForCheck();
        });
      }
    });
  }

  private handleUpdatedCrmMessage() {
    this.load().subscribe(() => {
      this.cdr.markForCheck();
      this.updatingCrm.set(false);
      this.snackBar.open('La gestión ha sido actualizada', 'Cerrar', { duration: 5000 });
    });
  }
}
